import React, { useState, useMemo, useCallback } from "react";
import { Notification } from "orcs-design-system";
import {
  getPersonEmailAttribute,
  getPersonMobileNumberAttribute,
  getPersonTelephoneAttribute,
  onPersonEmailClick,
  onPersonTelephoneClick,
} from "src/util/person";

import icons from "src/config/icons";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import { useOrgFeatureFlags } from "src/hooks/orgConfig";
import {
  MS_TEAMS_CHAT_LINK,
  contactDetailsCopy,
  getContactDetailsConfig,
} from "./utils";

const getContactLabel = (key, value, isCopiedState = false) => {
  if (isCopiedState && key === "email") {
    return contactDetailsCopy.email.successMessage;
  }

  const getAriaLabel = (labelKey, enabled) =>
    enabled
      ? contactDetailsCopy[labelKey].available
      : contactDetailsCopy[labelKey].unavailable;

  if (key === "microsoftTeams") {
    return getAriaLabel(key, !!value);
  }

  return `${getAriaLabel(key, !!value)}${value ? `: ${value}` : ""}`;
};

const usePersonContactDetailItems = ({ person }) => {
  const { loading: loadingOrgFeatureFlags, featureFlags: tenantFeatureFlags } =
    useOrgFeatureFlags();
  const contactOptions = useMemo(() => {
    if (loadingOrgFeatureFlags) {
      return null;
    }
    return getContactDetailsConfig(
      tenantFeatureFlags?.individualContactOptions
    );
  }, [loadingOrgFeatureFlags, tenantFeatureFlags?.individualContactOptions]);

  const [notification, setNotification] = useState(null);

  const [showCopiedState, setShowCopiedState] = useState(false);

  const phone =
    getPersonMobileNumberAttribute(person) ||
    getPersonTelephoneAttribute(person);

  const email = getPersonEmailAttribute(person);

  const contactDetailItems = useMemo(() => {
    if (contactOptions == null) {
      return [];
    }
    return [
      {
        icon: icons.mobile,
        visible: contactOptions.phone,
        disabled: !phone,
        label: getContactLabel("phone", phone?.value),
        ariaLabel: getContactLabel("phone", phone?.value),
        onClick: (event) => {
          const onError = () => {
            setNotification({
              message: contactDetailsCopy.phone.errorMessage,
              status: "error",
            });
          };
          onPersonTelephoneClick(person, onError)(event);
          trackEvent(EVENT_TRACKING.PERSON_CONTACTED, {
            contact_type: "phone",
          });
        },
      },
      {
        icon: showCopiedState ? icons.copied : icons.email,
        visible: contactOptions.email,
        disabled: !email?.value,
        label: getContactLabel("email", email?.value, showCopiedState),
        ariaLabel: getContactLabel("email", email?.value, showCopiedState),
        onClick: (event) => {
          onPersonEmailClick(person)(event);
          setShowCopiedState(true);
          setTimeout(() => {
            setShowCopiedState(false);
            setNotification(null);
          }, 3000);
          setNotification({
            message: contactDetailsCopy.email.successMessage,
            status: "success",
          });
          trackEvent(EVENT_TRACKING.PERSON_CONTACTED, {
            contact_type: "email",
          });
        },
      },
      {
        icon: icons.microsoftTeams,
        visible: contactOptions.microsoftTeams,
        disabled: !email?.value,
        label: getContactLabel("microsoftTeams", email?.value),
        ariaLabel: getContactLabel("microsoftTeams", email?.value),
        onClick: () => {
          window.open(
            `${MS_TEAMS_CHAT_LINK}users=${encodeURIComponent(email?.value)}`,
            "_blank",
            "noopener,noreferrer"
          );
          trackEvent(EVENT_TRACKING.PERSON_CONTACTED, {
            contact_type: "microsoft_teams",
          });
        },
      },
    ].filter((detail) => detail.visible);
  }, [contactOptions, phone, showCopiedState, email?.value, person]);

  const renderNotification = useCallback(() => {
    if (!notification) {
      return null;
    }

    return (
      <Notification
        variant={notification.status}
        onDismiss={() => setNotification(null)}
        centered
        floating
        closable={!showCopiedState}
        bottom="20px"
      >
        {notification.message}
      </Notification>
    );
  }, [notification, showCopiedState]);

  return {
    contactDetailItems,
    notification,
    showCopiedState,
    setNotification,
    setShowCopiedState,
    renderNotification,
  };
};

export default usePersonContactDetailItems;
